import React from 'react';
import { PageSection, SiteData } from '../../../types';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { isDarkBackground, getOptimalTextColor } from '../../../utils/contrast';

interface SectionProps {
  section: PageSection;
  siteData: SiteData;
  onNavigate?: (slug: string) => void;
}

export const AboutSection: React.FC<SectionProps> = ({ section, siteData, onNavigate }) => {
  const content = section.content || {};
  const style = section.style || {};

  const badge = content.badge || 'KURUMSAL KİMLİK';
  const title = content.title || 'Değer Üreten, Güven İnşa Eden Global Birikim';
  const description =
    content.description ||
    'Uzun yıllara dayanan tecrübemiz, etik değerlerimiz ve müşteri odaklı vizyonumuzla sektörde öncü standartları belirliyoruz. Güvenilir iş modelleri ve sürdürülebilir başarı için buradayız.';
  const imageUrl =
    content.imageUrl ||
    'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=1200&auto=format&fit=crop';
  const buttonText = content.buttonText || 'Daha Fazla Bilgi';
  const buttonLink = content.buttonLink || '/hakkimizda';

  const bgColor = style.backgroundColor || '#FFFFFF';
  const isDark = isDarkBackground(bgColor);
  const textColor = style.textColor || getOptimalTextColor(bgColor);
  const accentColor = siteData.settings?.accentColor || '#2563EB';

  const rawHighlights = content.highlights || content.items;
  const highlights = Array.isArray(rawHighlights) && rawHighlights.length > 0
    ? rawHighlights.map((h: any) => (typeof h === 'string' ? h : h.title || h.text))
    : [
        '25+ yıllık kurumsal ve operasyonel tecrübe',
        'Uluslararası kalite ve regülasyon standartları',
        'Kişiye ve kuruma özel esnek çözümler',
      ];

  const anchorId = style.anchorId || style.customId || 'about';
  const isReverse = Boolean(content.reverseLayout);

  return (
    <section
      id={anchorId}
      data-section-id={section.id}
      data-section-type="ABOUT"
      className="py-20 px-4 sm:px-6 lg:px-8 select-none transition-colors"
      style={{ backgroundColor: bgColor, color: textColor }}
    >
      <div className={`max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center ${isReverse ? 'lg:flex-row-reverse' : ''}`}>
        {/* Left Column: Image */}
        <div className={`relative rounded-3xl overflow-hidden shadow-2xl group aspect-4/3 lg:aspect-square bg-slate-100 ${isReverse ? 'lg:order-2' : ''}`}>
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 via-transparent to-transparent" />
        </div>

        {/* Right Column: Text Info */}
        <div className={`space-y-6 ${isReverse ? 'lg:order-1' : ''}`}>
          {badge && (
            <div
              className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full text-xs font-black tracking-widest uppercase border"
              style={{
                borderColor: `${accentColor}40`,
                backgroundColor: `${accentColor}10`,
                color: accentColor,
              }}
            >
              <Sparkles className="w-3 h-3" />
              <span>{badge}</span>
            </div>
          )}

          <h2 className={`text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {title}
          </h2>

          <p className={`text-sm sm:text-base leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
            {description}
          </p>

          <div className="space-y-3 pt-2">
            {highlights.map((item: string, idx: number) => (
              <div key={idx} className="flex items-center gap-3 text-xs sm:text-sm font-semibold">
                <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: accentColor }} />
                <span className={isDark ? 'text-slate-200' : 'text-slate-700'}>{item}</span>
              </div>
            ))}
          </div>

          {buttonText && (
            <div className="pt-4">
              <button
                type="button"
                onClick={() => onNavigate && onNavigate(buttonLink)}
                className="px-6 py-3.5 rounded-xl font-bold text-xs sm:text-sm shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all inline-flex items-center gap-2"
                style={{ backgroundColor: accentColor, color: getOptimalTextColor(accentColor) }}
              >
                <span>{buttonText}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
